import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';

import {
  Select,
  Input,
  Button,
  TextArea,
  DateInput,
  DataList,
} from 'next/components/ui';
import { PropertiesShape } from 'next/entities/rules';

import useToggle from 'next/hooks/use-toggle';

import usePropertyConditions from './use-property-conditions';
import { ConditionsContext } from './ConditionsContext';
import { OPERATORS } from './constants';
import { ClauseWrapper, Clause } from './styled';

export const PropertiesClause = ({ conditions, onChange }) => {
  const { properties: innerConditions } = conditions;
  const { property, operator, value } = innerConditions;

  const { propertyOptions, propertySuggestions } =
    useContext(ConditionsContext);

  const [showOptions, toggleExpanded] = useToggle(false);

  const handleChange = newInnerConditions =>
    onChange({ properties: newInnerConditions });

  const {
    draftValue,
    hasOptions,
    isRelativeTimeOperator,
    isAbsoluteTimeOperator,
    isNumericOperator,
    isDefaultOperator,
    isMultiValueOperator,
    onPropertyChange,
    onOperatorChange,
    onValueChange,
    onDateChange,
    onValueBlur,
    onValueSelected,
  } = usePropertyConditions(innerConditions, handleChange);

  const options = useMemo(() => {
    const opts = [...propertyOptions];

    if (
      property &&
      !opts
        .flatMap(({ options: subOptions }) => subOptions)
        .some(({ value: propertyName }) => propertyName === property)
    ) {
      opts.push({
        label: 'Unrecognized',
        options: [
          {
            label: property,
            value: property,
          },
        ],
      });
    }

    return opts;
  }, [propertyOptions, property]);

  const selectedPropertyOption = useMemo(
    () =>
      options
        .flatMap(group => group.options)
        .find(option => option.value === property),
    [options, property]
  );

  return (
    <ClauseWrapper>
      <Clause hasOptions={hasOptions}>
        <Select
          name="property"
          value={selectedPropertyOption}
          options={options}
          onChange={onPropertyChange}
          portal
        />
        <Select
          name="operator"
          value={OPERATORS.find(({ value: op }) => op === operator)}
          options={OPERATORS}
          onChange={onOperatorChange}
          portal
        />
        {isRelativeTimeOperator && (
          <>
            <Input
              type="number"
              aria-label="value"
              name="value"
              value={draftValue}
              onChange={onValueChange}
              onBlur={onValueBlur}
            />
            <span>
              {draftValue === 1 ? 'day' : 'days'}
              {['> ago', '< ago'].includes(operator) && ' ago'}
            </span>
          </>
        )}
        {isAbsoluteTimeOperator && (
          <DateInput future value={new Date(value)} onChange={onDateChange} />
        )}
        {isNumericOperator && (
          <Input
            type="number"
            aria-label="value"
            name="value"
            value={draftValue}
            onChange={onValueChange}
            onBlur={onValueBlur}
          />
        )}
        {isDefaultOperator && (
          <DataList
            aria-label="value"
            name="value"
            value={draftValue}
            suggestions={propertySuggestions?.[property]}
            onBlur={onValueBlur}
            onChange={onValueChange}
            onSelectedItemChange={onValueSelected}
          />
        )}
        {isMultiValueOperator && (
          <Button kind="secondary" onClick={toggleExpanded}>
            {showOptions ? 'Hide' : 'Show'} values
          </Button>
        )}
      </Clause>
      {showOptions && isMultiValueOperator && (
        <TextArea
          name="value"
          aria-label="value"
          placeholder="Enter values separated by commas or line breaks"
          value={draftValue}
          onChange={onValueChange}
          onBlur={onValueBlur}
        />
      )}
    </ClauseWrapper>
  );
};

PropertiesClause.propTypes = {
  conditions: PropertiesShape,
  onChange: PropTypes.func,
};
